import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Home, 
  Users, 
  ClipboardList, 
  JapaneseYen,
  FileText,
  Building2,
  LogOut,
  User,
  Menu,
  X,
  BookOpen,
  ShieldAlert,
  CreditCard,
  UserCircle,
  Calendar,
  Settings,
  Palette
} from 'lucide-react';
import { useState } from 'react';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isSystemAdmin = user?.role === 'system_admin';
  const isAdmin = isSystemAdmin || user?.role === 'facility_admin';

  const navItems = isSystemAdmin ? [
    { to: '/dashboard', icon: Home, label: 'ダッシュボード' },
    { to: '/admin', icon: Settings, label: 'システム管理' },
    { to: '/facilities', icon: Building2, label: '事業所管理' },
    { to: '/users', icon: Users, label: 'ユーザー管理' },
    { to: '/clients', icon: Users, label: '利用者管理' },
    { to: '/records', icon: ClipboardList, label: '支援記録' },
    { to: '/billing', icon: JapaneseYen, label: '請求管理' },
    { to: '/documents', icon: FileText, label: '書類管理' },
    { to: '/support-plans', icon: BookOpen, label: '個別支援計画' },
    { to: '/incidents', icon: ShieldAlert, label: 'インシデント管理' },
    { to: '/staff', icon: UserCircle, label: '職員管理' },
    { to: '/shifts', icon: Calendar, label: 'シフト管理' },
    { to: '/templates', icon: FileText, label: 'PDFテンプレート' },
    { to: '/pdf-design', icon: Palette, label: 'PDFデザイン編集' },
    { to: '/facility-settings', icon: Settings, label: '事業所設定' },
    { to: '/subscription', icon: CreditCard, label: 'プラン・課金' },
  ] : [
    { to: '/dashboard', icon: Home, label: 'ダッシュボード' },
    { to: '/clients', icon: Users, label: '利用者管理' },
    { to: '/records', icon: ClipboardList, label: '支援記録' },
    { to: '/billing', icon: JapaneseYen, label: '請求管理' },
    { to: '/documents', icon: FileText, label: '書類管理' },
    { to: '/support-plans', icon: BookOpen, label: '個別支援計画' },
    { to: '/incidents', icon: ShieldAlert, label: 'インシデント管理' },
    { to: '/staff', icon: UserCircle, label: '職員管理' },
    { to: '/shifts', icon: Calendar, label: 'シフト管理' },
    ...(isAdmin ? [
      { to: '/users', icon: Users, label: 'ユーザー管理' },
      { to: '/facilities', icon: Building2, label: '事業所管理' },
      { to: '/templates', icon: FileText, label: 'PDFテンプレート' },
      { to: '/pdf-design', icon: Palette, label: 'PDFデザイン編集' },
      { to: '/facility-settings', icon: Settings, label: '事業所設定' },
      { to: '/subscription', icon: CreditCard, label: 'プラン・課金' },
    ] : []),
  ];

  const getRoleLabel = (role) => {
    const labels = {
      system_admin: 'システム管理者',
      facility_admin: '事業所管理者',
      service_manager: 'サービス管理責任者',
      support_staff: '支援員',
      accountant: '経理担当',
      readonly: '閲覧専用'
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* トップバー */}
      <header className="bg-card border-b border-border h-16 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-50">
        <div className="flex items-center gap-4">
          {/* モバイルメニューボタン */}
          <Button 
            variant="ghost" 
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-button"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
          
          <div className="flex items-center gap-2">
            <img src="/logo192.png" alt="CLSS-SaaS" className="h-7 w-7 rounded" />
            <span className="font-semibold text-lg hidden sm:block">CLSS-SaaS</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-button">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User size={18} className="text-primary" />
                </div>
                <span className="hidden sm:block text-sm">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground mt-1">{getRoleLabel(user?.role)}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                <LogOut size={16} className="mr-2" />
                ログアウト
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex">
        {/* サイドバー */}
        <aside 
          className={`
            fixed lg:static inset-y-0 left-0 z-40 w-64 bg-card border-r border-border 
            transform transition-transform duration-200 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            pt-16 lg:pt-0
          `}
        >
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  isActive ? 'nav-item-active' : 'nav-item'
                }
                data-testid={`nav-${item.to.replace('/', '')}`}
              >
                <item.icon size={20} weight="duotone" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* オーバーレイ（モバイル） */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* メインコンテンツ */}
        <main className="flex-1 p-4 lg:p-8 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
